import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type {
  RolSistema,
  PaginatedResult,
  CrearRolRequest,
  ActualizarRolRequest,
  ActualizarPermisosRolRequest,
  RolPermisoDetallado,
  MatrizPermisos,
} from '@suminuta/shared';

// =====================================================
// QUERY KEYS
// =====================================================

export const rolesKeys = {
  all: ['roles'] as const,
  lists: () => [...rolesKeys.all, 'list'] as const,
  list: (filters?: {
    page?: number;
    limit?: number;
    busqueda?: string;
    soloActivos?: boolean;
  }) => [...rolesKeys.lists(), filters] as const,
  activos: () => [...rolesKeys.all, 'activos'] as const,
  base: () => [...rolesKeys.all, 'base'] as const,
  details: () => [...rolesKeys.all, 'detail'] as const,
  detail: (id: number) => [...rolesKeys.details(), id] as const,
  permisos: (rolId: number) => [...rolesKeys.all, 'permisos', rolId] as const,
  matriz: (rolId: number) => [...rolesKeys.all, 'matriz', rolId] as const,
};

// =====================================================
// QUERIES - ROLES
// =====================================================

/**
 * Hook para obtener roles con paginación y filtros
 */
export const useRoles = (filters?: {
  page?: number;
  limit?: number;
  busqueda?: string;
  soloActivos?: boolean;
}) => {
  return useQuery({
    queryKey: rolesKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.busqueda) params.append('busqueda', filters.busqueda);
      if (filters?.soloActivos !== undefined)
        params.append('soloActivos', filters.soloActivos.toString());

      const response = await api.get<
        { success: boolean } & PaginatedResult<RolSistema>
      >(`/roles-permisos/roles?${params.toString()}`);
      return response.data;
    },
    keepPreviousData: true,
  });
};

/**
 * Hook para obtener solo roles activos (para selects)
 */
export const useRolesActivos = () => {
  return useQuery({
    queryKey: rolesKeys.activos(),
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RolSistema[] }>(
        '/roles-permisos/roles/activos'
      );
      return response.data.data;
    },
  });
};

/**
 * Hook para obtener solo roles base del sistema
 */
export const useRolesBase = () => {
  return useQuery({
    queryKey: rolesKeys.base(),
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RolSistema[] }>(
        '/roles-permisos/roles/base'
      );
      return response.data.data;
    },
  });
};

/**
 * Hook para obtener un rol por ID
 */
export const useRol = (id: number) => {
  return useQuery({
    queryKey: rolesKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RolSistema }>(
        `/roles-permisos/roles/${id}`
      );
      return response.data.data;
    },
    enabled: !!id,
  });
};

// =====================================================
// QUERIES - PERMISOS DE ROLES
// =====================================================

/**
 * Hook para obtener todos los permisos de un rol
 */
export const usePermisosPorRol = (rolId: number) => {
  return useQuery({
    queryKey: rolesKeys.permisos(rolId),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: RolPermisoDetallado[];
      }>(`/roles-permisos/roles/${rolId}/permisos`);
      return response.data.data;
    },
    enabled: !!rolId,
  });
};

/**
 * Hook para obtener matriz de permisos de un rol
 */
export const useMatrizPermisos = (rolId: number) => {
  return useQuery({
    queryKey: rolesKeys.matriz(rolId),
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: MatrizPermisos;
      }>(`/roles-permisos/roles/${rolId}/matriz`);
      return response.data.data;
    },
    enabled: !!rolId,
  });
};

// =====================================================
// MUTATIONS - ROLES
// =====================================================

/**
 * Hook para crear un nuevo rol con sus permisos
 */
export const useCrearRol = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CrearRolRequest) => {
      const response = await api.post<{
        success: boolean;
        message: string;
        data: { id: number };
      }>('/roles-permisos/roles', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
};

/**
 * Hook para actualizar un rol
 */
export const useActualizarRol = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: ActualizarRolRequest;
    }) => {
      const response = await api.patch<{ success: boolean; message: string }>(
        `/roles-permisos/roles/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
      queryClient.invalidateQueries({
        queryKey: rolesKeys.detail(variables.id),
      });
    },
  });
};

/**
 * Hook para eliminar un rol
 */
export const useEliminarRol = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete<{ success: boolean; message: string }>(
        `/roles-permisos/roles/${id}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.all });
    },
  });
};

// =====================================================
// MUTATIONS - PERMISOS DE ROLES
// =====================================================

/**
 * Hook para actualizar todos los permisos de un rol
 */
export const useActualizarPermisosRol = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rolId,
      data,
    }: {
      rolId: number;
      data: ActualizarPermisosRolRequest;
    }) => {
      const response = await api.put<{ success: boolean; message: string }>(
        `/roles-permisos/roles/${rolId}/permisos`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: rolesKeys.permisos(variables.rolId),
      });
      queryClient.invalidateQueries({
        queryKey: rolesKeys.matriz(variables.rolId),
      });
    },
  });
};

/**
 * Hook para asignar un permiso específico a un rol
 */
export const useAsignarPermiso = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rolId,
      moduloId,
      permisoId,
    }: {
      rolId: number;
      moduloId: number;
      permisoId: number;
    }) => {
      const response = await api.post<{ success: boolean; message: string }>(
        `/roles-permisos/roles/${rolId}/permisos`,
        { modulo_id: moduloId, permiso_id: permisoId }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: rolesKeys.permisos(variables.rolId),
      });
      queryClient.invalidateQueries({
        queryKey: rolesKeys.matriz(variables.rolId),
      });
    },
  });
};

/**
 * Hook para remover un permiso específico de un rol
 */
export const useRemoverPermiso = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      rolId,
      moduloId,
      permisoId,
    }: {
      rolId: number;
      moduloId: number;
      permisoId: number;
    }) => {
      const response = await api.delete<{ success: boolean; message: string }>(
        `/roles-permisos/roles/${rolId}/permisos`,
        { data: { modulo_id: moduloId, permiso_id: permisoId } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: rolesKeys.permisos(variables.rolId),
      });
      queryClient.invalidateQueries({
        queryKey: rolesKeys.matriz(variables.rolId),
      });
    },
  });
};
